import { TicketStatuses } from "../../../../common/constants";
import type { TicketStatusLookupKey } from "../../../../common/constants";
import { wdpUtil } from "../../../../common/utils/util";
import { appLog } from "../../../../common/utils/app-log";
import { ButtonName, PermissionConfig, Role, TicketPermissions, TicketSection, TicketStatus } from "./types";


const permissionConfig: PermissionConfig = {
  operator: {
    notSubmitted: {
      viewTicket: true,
      editTicket: true,
      createTicket: true,
      deleteTicket: true,
      submitTicket: true,
      viewNotes: true,
      createNotes: true,
    },
    submitted: {
      viewTicket: true,
      viewNotes: true,
    },
    needsAttention: {
      viewTicket: true,
      editTicket: true,
      deleteTicket: true,
      submitTicket: true,
      viewNotes: true,
      createNotes: true,
    },
    inProgress: {
      viewTicket: true,
      viewNotes: true,
    },
    signedOff: {
      viewTicket: true,
      viewNotes: true,
      downloadPdf: true,
    },
    approved: {
      viewTicket: true,
      viewNotes: true,
      downloadPdf: true,
    },
    processed: {
      viewTicket: true,
      viewNotes: true,
      downloadPdf: true,
    },
  },
  operationalReviewer: {
    notSubmitted: {},
    submitted: {
      viewTicket: true,
      viewNotes: true,
      createNotes: true,
      verifyTicket: true,
      returnTicket: true,
    },
    needsAttention: {},
    inProgress: {},
    signedOff: {},
    approved: {},
    processed: {},
  },
  areaSignOff: {
    isInternal: true,
    notSubmitted: {
      viewNotes: true,
    },
    needsAttention: {
      viewNotes: true,
    },
    submitted: {
      viewTicket: true,
      deleteTicket: true,
      viewCostSummary: true,
      verifyTicket: true,
      returnTicket: true,
      viewNotes: true,
      createNotes: true,
    },
    inProgress: {
      viewTicket: true,
      editTicket: true,
      createTicket: true,
      deleteTicket: true,
      viewCostSummary: true,
      editCostSummary: true,
      returnTicket: true,
      signOffTicket: true,
      viewNotes: true,
      createNotes: true,
    },
    signedOff: {
      viewTicket: true,
      viewCostSummary: true,
      viewNotes: true,
      createNotes: true,
      unSignOffTicket: true,
      downloadPdf: true,
    },
    approved: {
      viewTicket: true,
      viewCostSummary: true,
      viewNotes: true,
      downloadPdf: true,
    },
    processed: {
      viewTicket: true,
      viewCostSummary: true,
      viewNotes: true,
      downloadPdf: true,
    },
  },
  finance: {
    isInternal: true,
    notSubmitted: {
      viewNotes: true,
    },
    needsAttention: {
      viewNotes: true,
    },
    submitted: {
      viewTicket: true,
      deleteTicket: true,
      viewCostSummary: true,
      viewNotes: true,
      createNotes: true,
      verifyTicket: true,
      returnTicket: true,
    },
    inProgress: {
      viewTicket: true,
      editTicket: true,
      createTicket: true,
      deleteTicket: true,
      viewCostSummary: true,
      editCostSummary: true,
      viewNotes: true,
      createNotes: true,
      returnTicket: true,
      signOffTicket: true,
    },
    signedOff: {
      viewTicket: true,
      viewCostSummary: true,
      editCostSummary: true,
      viewNotes: true,
      createNotes: true,
      unSignOffTicket: true,
      approveTicket: true,
      downloadPdf: true,
    },
    approved: {
      viewTicket: true,
      editCostSummary: true,
      viewCostSummary: true,
      viewNotes: true,
      unApproveTicket: true,
      downloadPdf: true,
    },
    processed: {
      viewTicket: true,
      viewCostSummary: true,
      viewNotes: true,
      downloadPdf: true,
    },
  },
};

// When a user has more than one app role, the highest-ranked one wins (checked in this order:
// finance > areaSignOff > operationalReviewer > operator). Matches the API.
export const getTicketRole = (auth: any): Role => {
  // return "finance"
  var role: Role = "unauthorized";
  if (wdpUtil.hasAppPermission(auth, "Eqp_Finance")) {
    role = "finance";
  } else if (wdpUtil.hasAppPermission(auth, "Eqp_AreaSignOff")) {
    role = "areaSignOff";
  } else if (wdpUtil.hasAppPermission(auth, "Eqp_OperationalReviewer")) {
    role = "operationalReviewer";
  } else if (wdpUtil.hasAppPermission(auth, "Eqp_Operator")) {
    role = "operator";
  }
  
  appLog.debug(`Determined ticket role: ${role}`);
  return role; // Default role if no other matches
};

const getTicketStatusFromId = (statusId: number): TicketStatus => {
  //["notSubmitted", "submitted", "needsAttention", "verified", "inProgress",
  // "signedOff", "approved", "processed"]
  var statusKey = TicketStatuses.find(
    (s) => s.lookupId === statusId,
  )?.lookupKey;
  var status: TicketStatus = "notSubmitted";
  switch (statusKey) {
    case "NOT-SUBMITTED":
      status = "notSubmitted";
      break;
    case "SUBMITTED":
      status = "submitted";
      break;
    case "NEEDS-ATTENTION":
      status = "needsAttention";
      break;
    case "IN-PROGRESS":
      status = "inProgress";
      break;
    case "SIGNED-OFF":
      status = "signedOff";
      break;
    case "APPROVED":
      status = "approved";
      break;
    case "PROCESSED":
      status = "processed";
      break;
    default:
      status = "notSubmitted";
  }
  appLog.debug(`Determined ticket status: ${status}`);
  return status;
};

export const getCurrentTicketPermissions = (auth: any, statusId: number) => {
  const role = getTicketRole(auth);
  if (role === "unauthorized") {
    appLog.warn("Unauthorized role detected. No permissions available.");
    return null;
  }
  const status = getTicketStatusFromId(statusId);
  const permissions = {
    ...permissionConfig[role]?.[status],
    isInternal: permissionConfig[role]?.isInternal === true,
  };
  appLog.debug(
    `Determined permissions for role: ${role}, status: ${status}`,
    permissions,
  );
  return permissions;
};

const statusKeyToLookupKey: Record<TicketStatus, TicketStatusLookupKey> = {
  notSubmitted: "NOT-SUBMITTED",
  submitted: "SUBMITTED",
  needsAttention: "NEEDS-ATTENTION",
  inProgress: "IN-PROGRESS",
  signedOff: "SIGNED-OFF",
  approved: "APPROVED",
  processed: "PROCESSED",
};

// Single source of truth for "which statuses can this role view a ticket in" - driven by the
// same permissionConfig used for the ticket details page, so dashboard visibility can't drift
// out of sync with actual view permissions.
export const getViewableStatusLookupKeys = (role: Role): TicketStatusLookupKey[] => {
  const roleConfig = permissionConfig[role];
  if (!roleConfig) return [];
  return (Object.keys(statusKeyToLookupKey) as TicketStatus[])
    .filter((status) => roleConfig[status]?.viewTicket === true)
    .map((status) => statusKeyToLookupKey[status]);
};

// Statuses offered in the dashboard search dropdown per role. For every role except operator this
// is exactly the set of statuses they can view a ticket in. Operator picks from a collapsed set of
// four; the API expands each option to the real statuses it stands for (Submitted covers
// In-Progress, Signed Off covers Approved and Processed).
const operatorSearchStatusOptions: TicketStatusLookupKey[] = ["NOT-SUBMITTED", "NEEDS-ATTENTION", "SUBMITTED", "SIGNED-OFF"];

export const getSearchableStatusLookupKeys = (role: Role): TicketStatusLookupKey[] => {
  if (role === "operator") {
    return operatorSearchStatusOptions;
  }
  return getViewableStatusLookupKeys(role);
};

// Single source of truth for "should this button be shown" - maps a button/section name to its
// matrix permission field, so the ticket details page, dashboard, and ticket-ctrl header can't
// drift out of sync on button visibility. Notes has no "edit" action in the matrix - "create" is
// the closest equivalent for whether new notes can be added. Per manager direction (2026-11-03),
// "addNote" is deliberately NOT keyed off createNotes/permissionConfig - notes can be left open
// regardless of ticket status, so anyone who can view OR edit the ticket can add a note.
export const canShowButton = (
  permissions: TicketPermissions | null | undefined,
  buttonName: ButtonName,
  section?: TicketSection,
  role?: Role,
): boolean => {
  switch (buttonName) {
    // A not-yet-created ticket has no resolved status, so when a role is supplied, "create" is
    // checked against every status bucket for that role instead of the single resolved
    // `permissions` snapshot - a new ticket lands in a different starting status per role
    // (operator -> notSubmitted, areaSignOff/finance -> inProgress).
    case "create": {
      if (!role) return permissions?.createTicket === true;
      const roleConfig = permissionConfig[role];
      return roleConfig
        ? (Object.keys(statusKeyToLookupKey) as TicketStatus[]).some((status) => roleConfig[status]?.createTicket === true)
        : false;
    }
    case "delete": return permissions?.deleteTicket === true;
    case "submit": return permissions?.submitTicket === true;
    case "verify": return permissions?.verifyTicket === true;
    case "return": return permissions?.returnTicket === true;
    case "signOff": return permissions?.signOffTicket === true;
    case "unsignOff": return permissions?.unSignOffTicket === true;
    case "approve": return permissions?.approveTicket === true;
    case "unapprove": return permissions?.unApproveTicket === true;
    case "downloadPdf": return permissions?.downloadPdf === true;
    case "addNote": return permissions?.viewTicket === true || permissions?.editTicket === true;
    case "edit": {
      const sectionPermission = section === "basic"      ? permissions?.editTicket
        : section === "meal"        ? permissions?.editTicket
        : section === "travel"      ? permissions?.editTicket
        : section === "work"        ? permissions?.editTicket
        : section === "costSummary" ? permissions?.editCostSummary
        : section === "notes"       ? permissions?.createNotes
        : undefined;
      return sectionPermission === true;
    }
    default: return false;
  }
};

// Post-sign-off Finance access grants editCostSummary without editTicket.
export const canEditCostSummaryField = (
  permissions: TicketPermissions | null | undefined,
  fieldName: string,
): boolean => {
  if (permissions?.editCostSummary !== true) return false;
  return permissions.editTicket === true
    || ["ratePerUnit", "internalOrder", "costCenter", "fund", "account"].includes(fieldName);
};

export const canManageCostSummaryRows = (
  permissions: TicketPermissions | null | undefined,
): boolean => permissions?.editCostSummary === true && permissions.editTicket === true;
